import React, { useState, useEffect, useMemo } from "react";
import {
  Paper, Typography, Container, Grid, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Box, FormControl, InputLabel,
  Select, MenuItem, Button, IconButton, Snackbar, Tooltip, CircularProgress,
  InputAdornment, Switch
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import SaveIcon from "@material-ui/icons/Save";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import PeopleAltIcon from "@material-ui/icons/PeopleAlt";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import LinkIcon from "@material-ui/icons/Link";
import SettingsIcon from "@material-ui/icons/Settings";
import HistoryIcon from "@material-ui/icons/History";
import MuiAlert from "@material-ui/lab/Alert";
import api from "../../services/api";

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const PLATFORMS = [
  { key: "Hotmart",    label: "Hotmart",    color: "#e8282b", bg: "rgba(232,40,43,0.08)",    abbr: "HT" },
  { key: "Kiwify",     label: "Kiwify",     color: "#00b37e", bg: "rgba(0,179,126,0.08)",    abbr: "KW" },
  { key: "Eduzz",      label: "Eduzz",      color: "#f5a623", bg: "rgba(245,166,35,0.08)",   abbr: "ED" },
  { key: "Monetizze",  label: "Monetizze",  color: "#5cb85c", bg: "rgba(92,184,92,0.08)",    abbr: "MN" },
  { key: "Appmax",     label: "Appmax",     color: "#7b2ff7", bg: "rgba(123,47,247,0.08)",   abbr: "AP" },
  { key: "Yampi",      label: "Yampi",      color: "#0078d4", bg: "rgba(0,120,212,0.08)",    abbr: "YM" },
];

const useStyles = makeStyles((theme) => ({
  root: { paddingTop: 32, paddingBottom: 48, background: "#f8fafc", minHeight: "100vh" },
  pageHeader: { marginBottom: 32 },
  pageTitle: { fontWeight: 800, fontSize: 26, color: "#0f172a", letterSpacing: "-0.02em" },
  pageSubtitle: { color: "#64748b", fontSize: 14, marginTop: 4, lineHeight: 1.6 },
  card: {
    borderRadius: 20,
    border: "1px solid rgba(15,23,42,0.07)",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 8px 28px rgba(0,0,0,0.06)",
    background: "#ffffff",
    padding: 28,
    marginBottom: 24,
    transition: "box-shadow .2s ease",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(0,0,0,0.06), 0 16px 40px rgba(0,0,0,0.08)",
    }
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 6,
  },
  cardIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  cardTitle: { fontWeight: 700, fontSize: 16, color: "#0f172a" },
  cardSubtitle: { fontSize: 13, color: "#64748b", marginBottom: 20, lineHeight: 1.6, paddingLeft: 52 },
  statCard: {
    borderRadius: 20,
    padding: "20px 24px",
    display: "flex",
    alignItems: "center",
    gap: 16,
    border: "1px solid rgba(15,23,42,0.06)",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 8px 28px rgba(0,0,0,0.05)",
    background: "#ffffff",
    transition: "transform .18s ease, box-shadow .18s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 24px rgba(0,0,0,0.08), 0 20px 48px rgba(0,0,0,0.06)",
    }
  },
  statIconWrap: {
    width: 52,
    height: 52,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  statNumber: { fontWeight: 800, fontSize: 32, color: "#0f172a", lineHeight: 1 },
  statLabel: { fontSize: 12, color: "#64748b", fontWeight: 500, marginTop: 2, textTransform: "uppercase", letterSpacing: "0.04em" },
  saveBtn: {
    marginTop: 24, paddingLeft: 28, paddingRight: 28,
    fontWeight: 700, borderRadius: 12, fontSize: 14,
    boxShadow: "0 4px 12px rgba(37,99,235,0.25)",
    "&:hover": { boxShadow: "0 8px 24px rgba(37,99,235,0.35)" }
  },
  urlBox: { display: "flex", alignItems: "center", gap: 8 },
  tableHeader: { fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "#6b7280" },
  typeBadge: {
    display: "inline-flex", alignItems: "center", gap: 4,
    padding: "3px 10px", borderRadius: 999,
    fontSize: 11, fontWeight: 700,
  },
  sectionDivider: { height: 1, background: "rgba(15,23,42,0.07)", marginBottom: 20 },
  /* Platform grid */
  platformGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(6, 1fr)",
    gap: 12,
    marginBottom: 32,
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "repeat(3, 1fr)",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
  },
  platformTile: {
    borderRadius: 16,
    border: "2px solid transparent",
    background: "#ffffff",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 4px 16px rgba(0,0,0,0.06)",
    padding: "16px 12px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    transition: "all .18s ease",
    userSelect: "none",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
    },
  },
  platformAbbr: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 900,
    fontSize: 14,
    letterSpacing: "0.02em",
  },
  platformName: {
    fontWeight: 700,
    fontSize: 12,
    color: "#374151",
    textAlign: "center",
    letterSpacing: "0.01em",
  },
  platformCount: {
    fontSize: 20,
    fontWeight: 800,
    lineHeight: 1,
  },
  platformCountLabel: {
    fontSize: 10,
    color: "#9ca3af",
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
}));

const statCards = (total, abandoned, approved) => [
  {
    value: total,
    label: "Total de Leads",
    icon: <PeopleAltIcon style={{ color: "#fff", width: 24, height: 24 }} />,
    bg: "linear-gradient(135deg,#6366f1 0%,#4f46e5 100%)",
    glow: "rgba(99,102,241,0.25)",
  },
  {
    value: abandoned,
    label: "Carrinhos Abandonados",
    icon: <ShoppingCartIcon style={{ color: "#fff", width: 24, height: 24 }} />,
    bg: "linear-gradient(135deg,#f59e0b 0%,#d97706 100%)",
    glow: "rgba(245,158,11,0.25)",
  },
  {
    value: approved,
    label: "Compras Aprovadas",
    icon: <CheckCircleIcon style={{ color: "#fff", width: 24, height: 24 }} />,
    bg: "linear-gradient(135deg,#10b981 0%,#059669 100%)",
    glow: "rgba(16,185,129,0.25)",
  },
];

const typeLabel = (t) => {
  if (!t) return "—";
  if (t === "abandoned") return "Carrinho Abandonado";
  if (t === "approved") return "Compra Aprovada";
  if (t === "flow") return "Gatilho Direto";
  if (t === "Abandono de Carrinho") return "Carrinho Abandonado";
  if (t === "Compra Aprovada") return "Compra Aprovada";
  if (t === "Pix Gerado") return "Pix Gerado";
  if (t === "Assinatura Cancelada") return "Cancelado";
  if (t === "Reembolso") return "Reembolso";
  return t;
};
const typeColor = (t) => {
  if (t === "abandoned" || t === "Abandono de Carrinho") return { bg: "#fef3c7", color: "#92400e" };
  if (t === "approved" || t === "Compra Aprovada") return { bg: "#d1fae5", color: "#065f46" };
  if (t === "Pix Gerado") return { bg: "#ede9fe", color: "#5b21b6" };
  if (t === "Assinatura Cancelada" || t === "Cancelado") return { bg: "#fee2e2", color: "#991b1b" };
  if (t === "Reembolso") return { bg: "#fce7f3", color: "#9d174d" };
  return { bg: "#e0e7ff", color: "#3730a3" };
};

export default function HotmartPage() {
  const classes = useStyles();
  const [whatsapps, setWhatsapps] = useState([]);
  const [flows, setFlows] = useState([]);
  const [config, setConfig] = useState({ hottok: "", whatsappId: "", pixReminderFlowId: "", pixReminderDelayMinutes: 3 });
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });
  const [activePlatform, setActivePlatform] = useState(null);

  const webhookUrl = "https://chat.auzaa.com/api/webhook/hotmart";

  useEffect(() => {
    Promise.all([
      api.get("/whatsapp/?session=0"),
      api.get("/hotmart/config"),
      api.get("/hotmart"),
      api.get("/flowbuilder"),
    ]).then(([wpRes, cfgRes, seqRes, flowRes]) => {
      setWhatsapps(Array.isArray(wpRes.data) ? wpRes.data : []);
      setFlows(Array.isArray(flowRes.data) ? flowRes.data : []);
      if (cfgRes.data) {
        setConfig({
          hottok: cfgRes.data.hottok || "",
          whatsappId: cfgRes.data.whatsappId || "",
          pixReminderFlowId: cfgRes.data.pixReminderFlowId || "",
          pixReminderDelayMinutes: cfgRes.data.pixReminderDelayMinutes || 3,

        });
      }
      setSequences(Array.isArray(seqRes.data) ? seqRes.data : []);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const platformCounts = useMemo(() => {
    const counts = {};
    PLATFORMS.forEach(p => { counts[p.key] = 0; });
    sequences.forEach(s => {
      const p = s.platform || "Hotmart";
      if (counts[p] !== undefined) counts[p]++;
      else counts[p] = 1;
    });
    return counts;
  }, [sequences]);

  const filteredSequences = useMemo(() => {
    if (!activePlatform) return sequences;
    return sequences.filter(s => (s.platform || "Hotmart") === activePlatform);
  }, [sequences, activePlatform]);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setSnack({ open: true, msg: "URL copiada!", severity: "success" });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post("/hotmart/config", {
        hottok: config.hottok || null,
        whatsappId: config.whatsappId || null,
        pixReminderFlowId: config.pixReminderFlowId || null,
        pixReminderDelayMinutes: parseInt(config.pixReminderDelayMinutes) || 3,
      });
      setSnack({ open: true, msg: "Configuração salva com sucesso!", severity: "success" });
    } catch {
      setSnack({ open: true, msg: "Erro ao salvar configuração.", severity: "error" });
    }
    setSaving(false);
  };

  const totalLeads = sequences.length;
  const abandoned = sequences.filter(s => s.type === "abandoned").length;
  const approved = sequences.filter(s => s.type === "approved").length;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" className={classes.root}>
      {/* Page header */}
      <Box className={classes.pageHeader}>
        <Typography className={classes.pageTitle}>
          Integrações & Webhooks
        </Typography>
        <Typography className={classes.pageSubtitle}>
          Configure as credenciais e o WhatsApp padrão para os gatilhos automáticos.
          Use o bloco <strong>Gatilho Webhook</strong> no FlowBuilder para criar fluxos por produto.
        </Typography>
      </Box>

      {/* Platform grid */}
      <div className={classes.platformGrid}>
        {PLATFORMS.map(p => {
          const isActive = activePlatform === p.key;
          return (
            <div
              key={p.key}
              className={classes.platformTile}
              onClick={() => setActivePlatform(isActive ? null : p.key)}
              style={{
                borderColor: isActive ? p.color : "transparent",
                background: isActive ? p.bg : "#ffffff",
                boxShadow: isActive
                  ? `0 0 0 2px ${p.color}22, 0 8px 24px ${p.color}18`
                  : "0 1px 3px rgba(0,0,0,0.05), 0 4px 16px rgba(0,0,0,0.06)",
              }}
            >
              <div
                className={classes.platformAbbr}
                style={{ background: p.bg, color: p.color }}
              >
                {p.abbr}
              </div>
              <Typography className={classes.platformName}>{p.label}</Typography>
              <Typography className={classes.platformCount} style={{ color: p.color }}>
                {platformCounts[p.key] || 0}
              </Typography>
              <Typography className={classes.platformCountLabel}>leads</Typography>
            </div>
          );
        })}
      </div>

      {/* Stat cards */}
      <Grid container spacing={3} style={{ marginBottom: 32 }}>
        {statCards(totalLeads, abandoned, approved).map((s, i) => (
          <Grid item xs={12} sm={4} key={i}>
            <Paper className={classes.statCard} elevation={0}>
              <Box
                className={classes.statIconWrap}
                style={{ background: s.bg, boxShadow: `0 6px 18px ${s.glow}` }}
              >
                {s.icon}
              </Box>
              <Box>
                <Typography className={classes.statNumber}>{s.value}</Typography>
                <Typography className={classes.statLabel}>{s.label}</Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Webhook URL */}
      <Paper className={classes.card} elevation={0}>
        <Box className={classes.cardHeader}>
          <Box className={classes.cardIcon} style={{ background: "rgba(99,102,241,0.10)" }}>
            <LinkIcon style={{ color: "#6366f1", width: 20, height: 20 }} />
          </Box>
          <Typography className={classes.cardTitle}>URL do Webhook Global (Hotmart)</Typography>
        </Box>
        <Typography className={classes.cardSubtitle}>
          Cole esta URL nas configurações da Hotmart para receber todos os eventos.
          Para fluxos específicos por produto, use o bloco <strong>Gatilho Webhook</strong> no FlowBuilder.
        </Typography>
        <Box className={classes.urlBox}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            value={webhookUrl}
            InputProps={{ readOnly: true, style: { fontFamily: "'JetBrains Mono', 'Fira Code', monospace", fontSize: 13, background: "#f8fafc" } }}
          />
          <Tooltip title="Copiar URL">
            <IconButton onClick={() => handleCopy(webhookUrl)} color="primary" style={{ border: "1px solid rgba(99,102,241,0.25)", borderRadius: 12, padding: 8 }}>
              <FileCopyOutlinedIcon style={{ width: 18, height: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Configuration */}
      <Paper className={classes.card} elevation={0}>
        <Box className={classes.cardHeader}>
          <Box className={classes.cardIcon} style={{ background: "rgba(245,158,11,0.10)" }}>
            <SettingsIcon style={{ color: "#f59e0b", width: 20, height: 20 }} />
          </Box>
          <Typography className={classes.cardTitle}>Configuração Global</Typography>
        </Box>
        <Typography className={classes.cardSubtitle}>
          O <strong>Hottok</strong> autentica os webhooks da Hotmart.
          O <strong>WhatsApp Padrão</strong> é usado em todos os gatilhos de fluxo.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Hottok (Token de Autenticação)"
              variant="outlined"
              size="small"
              type={showToken ? "text" : "password"}
              value={config.hottok}
              onChange={e => setConfig(c => ({ ...c, hottok: e.target.value }))}
              placeholder="Cole aqui o Hottok da Hotmart"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon fontSize="small" style={{ color: "#f59e0b" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowToken(v => !v)}>
                      {showToken ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 6, display: "block" }}>
              Encontre em: Hotmart → Ferramentas → Webhooks → Segurança
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl variant="outlined" fullWidth size="small">
              <InputLabel>WhatsApp Padrão</InputLabel>
              <Select
                value={config.whatsappId}
                onChange={e => setConfig(c => ({ ...c, whatsappId: e.target.value }))}
                label="WhatsApp Padrão"
              >
                <MenuItem value=""><em>Selecione uma conexão...</em></MenuItem>
                {whatsapps.map(w => (
                  <MenuItem key={w.id} value={w.id}>
                    {w.name}
                    {w.status === "CONNECTED" && (
                      <span style={{ marginLeft: 8, fontSize: 10, color: "#10b981", fontWeight: 700 }}>● conectado</span>
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 6, display: "block" }}>
              Conexão usada para enviar mensagens pelos gatilhos de fluxo
            </Typography>
          </Grid>


        </Grid>


        <Button
          variant="contained"
          color="primary"
          className={classes.saveBtn}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Salvando..." : "Salvar Configuração"}
        </Button>
      </Paper>

      {/* History */}
      <Paper className={classes.card} elevation={0}>
        <Box className={classes.cardHeader} style={{ justifyContent: "space-between" }}>
          <Box style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Box className={classes.cardIcon} style={{ background: "rgba(16,185,129,0.10)" }}>
              <HistoryIcon style={{ color: "#10b981", width: 20, height: 20 }} />
            </Box>
            <Typography className={classes.cardTitle}>
              Histórico de Leads
              {activePlatform && (
                <span style={{ marginLeft: 8, fontSize: 12, color: PLATFORMS.find(p => p.key === activePlatform)?.color, fontWeight: 600 }}>
                  — {activePlatform}
                </span>
              )}
            </Typography>
          </Box>
          {activePlatform && (
            <Button size="small" onClick={() => setActivePlatform(null)} style={{ color: "#6b7280", fontSize: 12 }}>
              Limpar filtro ✕
            </Button>
          )}
        </Box>
        <div className={classes.sectionDivider} />

        {filteredSequences.length === 0 ? (
          <Box textAlign="center" py={6}>
            <FlashOnIcon style={{ fontSize: 48, color: "#e2e8f0", marginBottom: 12 }} />
            <Typography variant="body2" color="textSecondary">
              {activePlatform ? `Nenhum lead de ${activePlatform} ainda.` : "Nenhum lead recebido ainda."}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Configure o webhook na plataforma para começar a receber leads.
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeader}>Lead</TableCell>
                <TableCell className={classes.tableHeader}>Produto</TableCell>
                <TableCell className={classes.tableHeader}>Evento</TableCell>
                <TableCell className={classes.tableHeader}>Status</TableCell>
                <TableCell className={classes.tableHeader}>Data</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSequences.map(s => {
                const tc = typeColor(s.type);
                const statusInfo = {
                  sent:               { label: "Enviado",          bg: "#d1fae5", color: "#065f46" },
                  filtered:           { label: "Filtrado",         bg: "#fef3c7", color: "#92400e" },
                  error:              { label: "Erro",              bg: "#fee2e2", color: "#991b1b" },
                  failed:             { label: "Erro",              bg: "#fee2e2", color: "#991b1b" },
                  pending:            { label: "Pendente",          bg: "#e0e7ff", color: "#3730a3" },
                  pix_pending:        { label: "Pix Aguardando",    bg: "#ede9fe", color: "#5b21b6" },
                  pix_paid:           { label: "Pix Pago",          bg: "#d1fae5", color: "#065f46" },
                  pix_reminder_sent:   { label: "1º Lembrete Enviado",  bg: "#d1fae5", color: "#065f46" },
                  pix_reminder_error:  { label: "Erro 1º Lembrete",    bg: "#fee2e2", color: "#991b1b" },
                  pix_reminder2_sent:  { label: "2º Lembrete Enviado",  bg: "#d1fae5", color: "#065f46" },
                  pix_reminder2_error: { label: "Erro 2º Lembrete",    bg: "#fee2e2", color: "#991b1b" },
                }[s.status] || { label: s.status, bg: "#f3f4f6", color: "#374151" };
                return (
                  <TableRow key={s.id} hover>
                    <TableCell>
                      <Typography variant="body2" style={{ fontWeight: 600, color: "#0f172a" }}>{s.customerName}</Typography>
                      <Typography variant="caption" style={{ color: "#6b7280" }}>{s.customerPhone}</Typography>
                    </TableCell>
                    <TableCell style={{ color: "#374151", fontSize: 13 }}>{s.productName}</TableCell>
                    <TableCell>
                      <span className={classes.typeBadge} style={{ background: tc.bg, color: tc.color }}>
                        {typeLabel(s.type)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={classes.typeBadge} style={{ background: statusInfo.bg, color: statusInfo.color }}>
                        {statusInfo.label}
                      </span>
                    </TableCell>
                    <TableCell style={{ color: "#6b7280", fontSize: 12 }}>
                      {new Date(s.createdAt).toLocaleString("pt-BR")}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity}>{snack.msg}</Alert>
      </Snackbar>
    </Container>
  );
}
